// Los mensajes que se le mandan al cliente por el enlace de renovacion.
//
// Lissette, 2026-09-08: *"poder cambiar en el modulo de renovacion el mensaje
// predeterminado y que quede fijo para todos de una vez, predeterminado en
// ingles y espanol"*.
//
// Antes estos cuatro textos estaban escritos a fuego dentro de deskte-full.jsx.
// Se podian editar en la ventana de envio, pero SOLO para ese cliente y esa
// vez: al siguiente volvia a salir el de fabrica. Quien queria su propia
// redaccion la reescribia en cada envio, y cada persona del equipo mandaba una
// version distinta.
//
// Aqui vive SOLO la logica, sin nada de pantalla, para poder probarla sin
// montar React.

/** Las variables que se pueden escribir en la plantilla. */
export const VARIABLES = ['{nombre}', '{agente}', '{enlace}'];

/**
 * Tope de la plantilla guardada.
 *
 * El backend rechaza un SMS de mas de 1600 caracteres. Se guarda con holgura
 * (900) porque al enviar todavia entran el nombre del cliente, el nombre del
 * agente y el enlace. Se corta al GUARDAR, donde se ve el numero, y nunca al
 * enviar: un mensaje cortado a la mitad camino del cliente seria peor que un
 * error en pantalla.
 */
export const LARGO_MAXIMO = 900;

/**
 * Los textos de fabrica: palabra por palabra los que habia en el codigo, para
 * que una agencia que no toque nada siga mandando exactamente lo de siempre.
 */
export const PLANTILLAS_DE_FABRICA = {
  noticeEs: 'Hola {nombre}, soy {agente}. Te preparé la revisión de tu renovación. ¿Quieres verla? Respóndeme SÍ.',
  noticeEn: "Hi {nombre}, this is {agente}. I've prepared your renewal review. Want to see it? Reply YES.",
  linkEs: 'Hola {nombre}, aquí está tu revisión de renovación: {enlace} — revisa tus datos y dime si todo está correcto. — {agente}',
  linkEn: "Hi {nombre}, here's your renewal review: {enlace} — please check your info and let me know if it's all correct. — {agente}",
};

/** La llave dentro del ajuste: 'notice'|'link' + idioma. */
export function llaveDe(tipo, lang) {
  const t = tipo === 'notice' ? 'notice' : 'link';
  return t + (lang === 'es' ? 'Es' : 'En');
}

/** La plantilla que toca: la de la agencia si escribió una, si no la de fábrica. */
export function plantillaDe(mensajes, tipo, lang) {
  const llave = llaveDe(tipo, lang);
  const propia = mensajes && typeof mensajes === 'object' ? mensajes[llave] : null;
  const texto = typeof propia === 'string' ? propia.trim() : '';
  return texto || PLANTILLAS_DE_FABRICA[llave];
}

/**
 * El mensaje ya listo para ese cliente.
 *
 * Si la plantilla del ENLACE se quedó sin `{enlace}` —alguien lo borró sin
 * darse cuenta— el enlace se pega al final igualmente. Un SMS de renovación
 * sin enlace no es un mensaje con un fallo: es un mensaje inútil, y el cliente
 * no tiene forma de saber que le falta algo. El editor además no deja guardarla
 * así; esto es el cinturón por si una plantilla vieja ya venía rota.
 */
export function componerMensaje({ mensajes, tipo, lang, nombre, agente, enlace }) {
  const esEnlace = tipo !== 'notice';
  const plantilla = plantillaDe(mensajes, tipo, lang);
  let texto = plantilla
    .split('{nombre}').join(String(nombre || '').trim())
    .split('{agente}').join(String(agente || '').trim())
    .split('{enlace}').join(esEnlace ? String(enlace || '').trim() : '');
  texto = texto.replace(/[ \t]{2,}/g, ' ').trim();
  if (esEnlace && enlace && !texto.includes(enlace)) texto = `${texto} ${enlace}`.trim();
  return texto;
}

/**
 * ¿Se puede guardar esta plantilla? Devuelve el motivo, o null si está bien.
 *
 * Dos reglas, y las dos existen por lo que pasaría si no estuvieran:
 *
 *  - La del ENLACE tiene que llevar `{enlace}`. Sin eso el cliente recibe un
 *    texto que le pide revisar algo y no le dice dónde.
 *  - La del AVISO NO puede llevarlo. El aviso existe para preguntar antes de
 *    mandar nada ("¿quieres verla? respóndeme SÍ"); meterle el enlace se salta
 *    esa pregunta, que es justo lo que el paso en dos partes viene a proteger.
 */
export function problemaDeLaPlantilla(tipo, texto, lang) {
  const es = lang === 'es';
  const cuerpo = String(texto || '').trim();
  if (!cuerpo) return es ? 'El mensaje no puede quedar vacío.' : 'The message cannot be empty.';
  const llevaEnlace = cuerpo.includes('{enlace}');
  if (tipo === 'notice' && llevaEnlace) {
    return es
      ? 'El aviso no lleva {enlace}: sirve para preguntar antes de mandarlo. El enlace va en el segundo mensaje.'
      : 'The notice cannot carry {enlace}: it asks first. The link goes in the second message.';
  }
  if (tipo !== 'notice' && !llevaEnlace) {
    return es
      ? 'Al mensaje del enlace le falta {enlace}. Sin eso el cliente no recibe la dirección.'
      : 'The link message is missing {enlace}. Without it the client gets no address.';
  }
  return null;
}

/** Deja el ajuste listo para guardar: solo las cuatro llaves, recortadas. */
export function limpiarMensajes(mensajes) {
  const salida = {};
  for (const llave of Object.keys(PLANTILLAS_DE_FABRICA)) {
    const v = mensajes && typeof mensajes === 'object' ? mensajes[llave] : null;
    const texto = typeof v === 'string' ? v.trim().slice(0, LARGO_MAXIMO) : '';
    // Lo que quede IGUAL que el de fabrica no se guarda: asi una agencia que
    // no ha cambiado nada sigue heredando las mejoras del texto de fabrica en
    // vez de quedarse congelada con una copia del de hoy.
    if (texto && texto !== PLANTILLAS_DE_FABRICA[llave]) salida[llave] = texto;
  }
  return salida;
}

/**
 * Las variables `{…}` que lleva un texto, sin repetir.
 *
 * Se usa para vigilar una traducción: el modelo tiene instrucciones de dejar
 * lo que va entre llaves intacto, pero "tiene instrucciones" no es "lo hizo".
 */
export function variablesDe(texto) {
  const encontradas = String(texto || '').match(/\{[a-zA-Z]+\}/g) || [];
  return [...new Set(encontradas)];
}

/**
 * ¿Se puede aceptar esta traducción? Devuelve el motivo, o null si está bien.
 *
 * SE RECHAZA Y NO SE ARREGLA A MEDIAS. Si la traducción perdió `{enlace}` no
 * se puede pegar de vuelta en su sitio —el sitio depende de la frase—, y
 * dejarla puesta en el cuadro invita a guardarla. Se deja el texto anterior y
 * se dice qué faltó, que es algo que la persona puede corregir; un mensaje
 * roto guardado no lo ve nadie hasta que le llega a un cliente.
 */
export function problemaDeLaTraduccion(original, traducido, lang) {
  const es = lang === 'es';
  const cuerpo = String(traducido || '').trim();
  if (!cuerpo) {
    return es
      ? 'La traducción llegó vacía. Se dejó el texto como estaba.'
      : 'The translation came back empty. The text was left as it was.';
  }
  const perdidas = variablesDe(original).filter((v) => !cuerpo.includes(v));
  if (perdidas.length) {
    return es
      ? `La traducción perdió ${perdidas.join(' y ')}. Se dejó el texto como estaba: sin eso el mensaje sale incompleto.`
      : `The translation dropped ${perdidas.join(' and ')}. The text was left as it was: without that the message goes out incomplete.`;
  }
  return null;
}
